import React, { useState, useEffect } from 'react';
import { signInWithEmailAndPassword, signOut, sendPasswordResetEmail, createUserWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, setDoc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import googleLogo from '../img/Google-Icon.png';
import { auth } from '../config/firebase-config';
import './Login.css';

// Initialize the GoogleAuthProvider and add the necessary scopes
const provider = new GoogleAuthProvider();
provider.addScope('https://www.googleapis.com/auth/forms.body');
provider.addScope('https://www.googleapis.com/auth/presentations');
provider.addScope('https://www.googleapis.com/auth/drive.file');

const db = getFirestore();

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [resetEmail, setResetEmail] = useState('');
    const [error, setError] = useState('');
    const [showResetForm, setShowResetForm] = useState(false);
    const [isSignup, setIsSignup] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const signOutUser = async () => {
            await signOut(auth);
        };
        signOutUser();
    }, []);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        let timeout;
        const handleActivity = () => {
            clearTimeout(timeout);
            timeout = setTimeout(async () => {
                await signOut(auth);
                navigate('/login');
            }, 900000); // 15 minutes in milliseconds
        };

        window.addEventListener('mousemove', handleActivity);
        window.addEventListener('keypress', handleActivity);

        return () => {
            window.removeEventListener('mousemove', handleActivity);
            window.removeEventListener('keypress', handleActivity);
            clearTimeout(timeout);
        };
    }, [navigate]);

    const handlePasswordReset = async (event) => {
        event.preventDefault();
        if (!resetEmail) {
            setError("Please enter your email address for password reset.");
            return;
        }
        setIsLoading(true);
        try {
            await sendPasswordResetEmail(auth, resetEmail);
            setError("Password reset email sent. Please check your inbox.");
            setResetEmail('');
            setShowResetForm(false);
        } catch (error) {
            console.error(error);
            setError("Failed to send password reset email.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate('/');
        } catch (error) {
            console.error("Logout failed: ", error);
        }
    };

    const handleLogin = async (event) => {
        event.preventDefault();
        setIsLoading(true);
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;
    
            const userDocRef = doc(db, 'users', user.uid);
            const userDoc = await getDoc(userDocRef);
    
            let accountType = '';
    
            if (userDoc.exists()) {
                // Get accountType from Firebase if the user document exists
                const userData = userDoc.data();
                accountType = userData.account || '';
            } else {
                // If user document does not exist, create it and set accountType to ''
                await setDoc(userDocRef, {
                    email: user.email,
                    paymentStatus: 'unpaid',
                    account: '',
                    lastResetDate: new Date(),
                });
                accountType = '';
            }
    
            // Store accountType in local storage
            localStorage.setItem('accountType', accountType);
    
            // Navigate to dashboard
            navigate('/dashboard');
        } catch (error) {
            console.error(error);
            setError("Failed to log in. Check your email and password.");
        } finally {
            setIsLoading(false);
        }
    };    

    const handleSignup = async (event) => {
        event.preventDefault();
        if (password !== confirmPassword) {
            setError("Passwords do not match!");
            return;
        }
        setIsLoading(true);
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            const userDocRef = doc(db, 'users', user.uid);
            const userDoc = await getDoc(userDocRef);

            if (!userDoc.exists()) {
                await setDoc(userDocRef, {
                    email: email,
                    paymentStatus: 'unpaid',
                    account: '',
                    lastResetDate: new Date(),
                });
            }

            sessionStorage.setItem('accountSetupComplete', 'true');
            sessionStorage.setItem('userEmail', email);
            navigate('/pricing', { state: { email } });
        } catch (error) {
            console.error("Error creating account:", error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        setIsLoading(true);
        try {
            const result = await signInWithPopup(auth, provider);
            const credential = GoogleAuthProvider.credentialFromResult(result);
            const token = credential.accessToken;
            const user = result.user;
    
            const userDocRef = doc(db, 'users', user.uid);
            const userDoc = await getDoc(userDocRef);
    
            let accountType = '';
    
            if (userDoc.exists()) {
                const userData = userDoc.data();
                accountType = userData.account || '';
            } else {
                await setDoc(userDocRef, {
                    email: user.email,
                    paymentStatus: 'unpaid',
                    account: '',
                    lastResetDate: new Date(),
                });
                accountType = '';
            }
    
            localStorage.setItem('accountType', accountType);
            sessionStorage.setItem('googleAuthToken', token);
    
            if (accountType === '') {
                navigate('/pricing');
            } else {
                navigate('/dashboard');
            }
    
        } catch (error) {
            console.error('Failed to log in with Google:', error);
            setError('Failed to log in with Google.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div id="login">
            <div className="login-container">
                <div className="login-card-container">
                    <div className="login-form-wrapper">
                        <div className="login-section-title">
                            <h2>{showResetForm ? "Reset Password" : (isSignup ? "Create an account" : "Welcome back")}</h2>
                            {error && <p className="login-error-message">{error}</p>}
                        </div>
                        
                        <form className="login-form" onSubmit={showResetForm ? handlePasswordReset : (isSignup ? handleSignup : handleLogin)}>
                            <div className="login-input-group">
                                <label htmlFor="email">Email address</label>
                                <input
                                    id="email"
                                    type="email"
                                    value={isSignup ? email : (showResetForm ? resetEmail : email)}
                                    onChange={e => isSignup ? setEmail(e.target.value) : (showResetForm ? setResetEmail(e.target.value) : setEmail(e.target.value))}
                                    required
                                />
                            </div>

                            {!showResetForm && (
                                <div className="login-input-group">
                                    <label htmlFor="password">Password</label>
                                    <input
                                        id="password"
                                        type="password"
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                        required
                                    />
                                </div>
                            )}

                            {isSignup && (
                                <div className="login-input-group">
                                    <label htmlFor="confirmPassword">Confirm Password</label>
                                    <input
                                        id="confirmPassword"
                                        type="password"
                                        value={confirmPassword}
                                        onChange={e => setConfirmPassword(e.target.value)}
                                        required
                                    />
                                </div>
                            )}

                            <button type="submit" className="login-button" disabled={isLoading}>
                                {isLoading ? "Processing..." : (showResetForm ? "Send Reset Email" : (isSignup ? "Sign Up" : "Continue"))}
                            </button>

                            {!showResetForm && !isSignup && currentUser && (
                                <button type="button" onClick={handleLogout} className="login-logout-button" disabled={isLoading}>Logout</button>
                            )}

                            {!showResetForm && (
                                <div className="login-form-links">
                                    {!isSignup && (
                                        <p className="login-form-toggle login-forgot-link" onClick={() => setShowResetForm(true)}>
                                            Forgot password?
                                        </p>
                                    )}

                                    <p className="login-form-toggle login-account-toggle" onClick={() => setIsSignup(!isSignup)}>
                                        {isSignup ? "Already have an account? Login" : "Don't have an account? Sign Up"}
                                    </p>
                                </div>
                            )}

                            {showResetForm && (
                                <p className="login-form-toggle login-back-link" onClick={() => setShowResetForm(false)}>
                                    Back to Login
                                </p>
                            )}

                            {!showResetForm && (
                                <button 
                                    type="button" 
                                    onClick={handleGoogleLogin} 
                                    className="login-google-login-button"
                                    disabled={isLoading}
                                >
                                    <img src={googleLogo} alt="Google Logo" className="login-google-logo" />
                                    Continue with Google
                                </button>
                            )}
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;